import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { METIER_TYPES, METIER_LIST } from "@/lib/metier-types";
import {
  Wrench, Zap, Home, Hammer, Key, Layers, Building, TreePine, Square,
  UtensilsCrossed, Trees, Flame, Bath, Warehouse, Bell, DoorClosed, Wind,
  Snowflake, Shovel, Paintbrush, ArrowRight, Lock, Camera, BadgeCheck, MapPin,
  Grid3x3,
} from "lucide-react";

const ICONS = {
  Wrench, Zap, Home, Hammer, Key, Layers, Building, TreePine, Square,
  UtensilsCrossed, Trees, Flame, Bath, Warehouse, Bell, DoorClosed, Wind,
  Snowflake, Shovel, Paintbrush, Grid3x3,
  Bricks: Layers,
};

function buildMetierSEO(m) {
  return {
    title: `${m.label} : trouver un ${m.label.toLowerCase()} de confiance · ${SITE_BRAND}`,
    description: `${m.intro} Trouvez un ${m.label.toLowerCase()} SIRET vérifié, comparez les devis et payez uniquement quand vous validez les travaux. Prix ${m.hourlyRate[0]}-${m.hourlyRate[1]}€/h.`,
    keywords: `${m.keywords}, ${m.label.toLowerCase()} SIRET vérifié, devis ${m.label.toLowerCase()} gratuit, ${m.label.toLowerCase()} paiement sécurisé, ${m.label.toLowerCase()} Batisur`,
    canonical: `/artisan/${m.slug}`,
    ogType: "website",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `${m.label} — ${SITE_BRAND}`,
        "description": m.intro,
        "provider": { "@type": "Organization", "name": SITE_BRAND, "url": SITE_URL },
        "areaServed": "France",
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "EUR",
          "lowPrice": m.interventionRange[0],
          "highPrice": m.interventionRange[1],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [{
          "@type": "Question",
          "name": m.faqTitle,
          "acceptedAnswer": { "@type": "Answer", "text": m.faqBody },
        }],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Artisans", "item": `${SITE_URL}/artisan` },
          { "@type": "ListItem", "position": 3, "name": m.label, "item": `${SITE_URL}/artisan/${m.slug}` },
        ],
      },
    ],
  };
}

const CITIES_SHORTLIST = [
  { slug: "paris", label: "Paris" },
  { slug: "lyon", label: "Lyon" },
  { slug: "marseille", label: "Marseille" },
  { slug: "bordeaux", label: "Bordeaux" },
  { slug: "toulouse", label: "Toulouse" },
  { slug: "nantes", label: "Nantes" },
  { slug: "lille", label: "Lille" },
  { slug: "strasbourg", label: "Strasbourg" },
  { slug: "rennes", label: "Rennes" },
  { slug: "montpellier", label: "Montpellier" },
  { slug: "nice", label: "Nice" },
  { slug: "grenoble", label: "Grenoble" },
  { slug: "angers", label: "Angers" },
  { slug: "rouen", label: "Rouen" },
  { slug: "reims", label: "Reims" },
  { slug: "le-havre", label: "Le Havre" },
  { slug: "toulon", label: "Toulon" },
];

export default function MetierPage() {
  const { metier: slug } = useParams();
  const m = METIER_TYPES[slug];
  if (!m) return <Navigate to="/artisan" replace />;

  const Icon = ICONS[m.icon] || Wrench;

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid={`metier-${m.slug}`}>
      <SEO cfg={buildMetierSEO(m)} />
      <Navbar />

      {/* HERO */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 relative">
          <nav className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-5" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/artisan" className="hover:text-[#FF4D00]">Artisans</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF4D00]">{m.label}</span>
          </nav>
          <div className="inline-flex items-center gap-3">
            <div className="w-14 h-14 border-2 border-white bg-[#FF4D00] flex items-center justify-center">
              <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Métier BTP</div>
          </div>
          <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-5 max-w-4xl leading-[0.95]">
            Trouver un <span className="text-[#FF4D00]">{m.label.toLowerCase()}</span> de confiance
          </h1>
          <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-3xl">
            {m.intro} Batisur vérifie chaque {m.label.toLowerCase()} SIRET auprès de l'INSEE, sécurise votre paiement en séquestre Stripe et documente chaque étape par preuves photo horodatées.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/register?role=demandeur&metier=${m.slug}`}>
              <button className="btn-batisur flex items-center gap-2" data-testid={`metier-cta-${m.slug}`}>
                Publier ma demande <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/estimer">
              <button className="btn-anthracite border-2 border-white/40">
                Estimer mon budget
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* PRIX */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Prix indicatifs France 2026</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          Combien coûte un <span className="text-[#FF4D00]">{m.label.toLowerCase()}</span> ?
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid={`metier-pricing-${m.slug}`}>
          <div className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#0A0A0B]">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">// Tarif horaire</div>
            <div className="display text-3xl mt-2">{m.hourlyRate[0]}€ - {m.hourlyRate[1]}€ <span className="text-base font-normal">/h</span></div>
            <p className="text-sm text-zinc-600 mt-2">Tarif horaire moyen HT en France, hors déplacement.</p>
          </div>
          <div className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#FF4D00]">
            <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Intervention</div>
            <div className="display text-3xl mt-2">{m.interventionRange[0]}€ - {m.interventionRange[1]}€</div>
            <p className="text-sm text-zinc-600 mt-2">Coût moyen d'une intervention ponctuelle TTC.</p>
          </div>
          <div className="border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-6">
            <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// {m.exampleProject.name}</div>
            <div className="display text-3xl mt-2 text-white">{m.exampleProject.price[0].toLocaleString('fr-FR')}€ - {m.exampleProject.price[1].toLocaleString('fr-FR')}€</div>
            <p className="text-sm text-zinc-400 mt-2">Fourchette de prix TTC pour ce type de chantier complet.</p>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-6">
          * Prix indicatifs pour un projet standard en 2026, hors déplacement et matériaux spécifiques. Publiez votre demande pour obtenir des devis réels comparables.
        </p>
      </section>

      {/* SERVICES */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Prestations</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
            Ce que fait un <span className="text-[#FF4D00]">{m.label.toLowerCase()}</span>
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {m.services.map((s, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-5 py-1">
                <h3 className="display text-lg leading-tight">{s}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 PROTECTIONS */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// 3 protections Batisur</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          Payez votre {m.label.toLowerCase()} <span className="text-[#FF4D00]">sans risque.</span>
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { i: Lock, t: "Séquestre Stripe", d: `Vos fonds pour le ${m.label.toLowerCase()} bloqués chez Stripe (agréé bancaire européen), libérés seulement après validation.` },
            { i: Camera, t: "Preuves photos", d: `Chaque étape prouvée par photo GPS + timestamp SHA-256 opposable devant assurance décennale.` },
            { i: BadgeCheck, t: "SIRET vérifié Sirene", d: `Chaque ${m.label.toLowerCase()} contrôlé en temps réel via l'API Sirene/INSEE. Zéro faux professionnel.` },
          ].map(({ i: PI, t, d }, idx) => (
            <div key={idx} className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#0A0A0B]">
              <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center mb-4">
                <PI className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div className="display text-lg leading-tight">{t}</div>
              <p className="text-sm text-zinc-700 mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// FAQ</div>
          <h2 className="display text-3xl mt-3">{m.faqTitle}</h2>
          <p className="text-base text-zinc-700 mt-4 leading-relaxed">{m.faqBody}</p>
        </div>
      </section>

      {/* CITIES LINKS */}
      <section className="bg-[#F7F7F9] border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// {m.label} par ville</div>
          <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3">
            Trouver un {m.label.toLowerCase()} près de chez vous
          </h2>
          <p className="text-sm text-zinc-600 mt-3 max-w-2xl">
            Batisur référence les <strong>{m.plural.toLowerCase()}</strong> dans <strong>plus de 300 villes de France</strong> — de Paris à Angoulême, de Lyon à Biarritz, en passant par Chamonix ou Perpignan. Voici les principales :
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid={`metier-cities-${m.slug}`}>
            {CITIES_SHORTLIST.map((c) => (
              <Link
                key={c.slug}
                to={`/artisan/${m.slug}/${c.slug}`}
                className="border-2 border-[#0A0A0B] bg-white px-4 py-3 hover:bg-[#FF4D00] hover:text-white transition flex items-center gap-2"
                data-testid={`metier-city-link-${m.slug}-${c.slug}`}
              >
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-sm font-semibold">{m.label} {c.label}</span>
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/artisans/villes" className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold hover:underline">
              Voir toutes les villes couvertes →
            </Link>
          </div>
        </div>
      </section>

      {/* OTHER METIERS */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">// Autres métiers BTP</div>
          <h2 className="display text-3xl mt-3">Explorer les 25 métiers du bâtiment</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {METIER_LIST.filter((x) => x.slug !== m.slug).slice(0, 12).map((x) => (
              <Link
                key={x.slug}
                to={`/artisan/${x.slug}`}
                className="mono text-[10px] uppercase tracking-widest border border-zinc-300 bg-white px-3 py-1.5 hover:border-[#FF4D00] hover:text-[#FF4D00] transition"
              >
                {x.label}
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/artisan" className="text-[#FF4D00] font-semibold text-sm underline">
              Voir tous les métiers du BTP →
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">// Prêt ?</div>
            <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              Trouvez votre {m.label.toLowerCase()} en 5 minutes
            </h2>
          </div>
          <Link to={`/register?role=demandeur&metier=${m.slug}`}>
            <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2">
              Publier ma demande <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
